import axios from 'axios';
import { getApiBaseUrl } from './api';

// API 기본 URL (api.js와 동일한 로직 사용)
const API_BASE_URL = getApiBaseUrl();

// axios 인스턴스 생성
const authApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

// 토큰 가져오기 헬퍼 함수
const getToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
};

// 요청 인터셉터: 토큰 자동 포함
authApi.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 토큰 갱신 중복 방지 플래그
let isRefreshing = false;
let failedQueue = [];

// 대기 중인 요청 처리
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// 스토리지 정리 및 로그아웃 처리
const clearAuthAndRedirect = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('rememberMe');

    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

// 응답 인터셉터: 토큰 갱신 로직 포함
authApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // 401 에러이고, 재시도하지 않은 요청인 경우
        if (error.response?.status === 401 && !originalRequest._retry) {
            // refresh 요청 자체가 실패한 경우 로그아웃
            if (originalRequest.url?.includes('/auth/refresh')) {
                clearAuthAndRedirect();
                return Promise.reject(error);
            }

            // 이미 토큰 갱신 중인 경우 대기열에 추가
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return authApi(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // 토큰 갱신 시도
                const response = await authApi.post('/auth/refresh');
                // ApiResponse 형식: { success, data: { token }, ... }
                const token = response.data.data?.token || response.data.token;

                // 새 토큰 저장
                const rememberMe = localStorage.getItem('rememberMe') === 'true';
                const storage = rememberMe ? localStorage : sessionStorage;
                storage.setItem('token', token);

                // 대기 중인 요청 처리
                processQueue(null, token);

                // 원래 요청 재시도
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return authApi(originalRequest);
            } catch (refreshError) {
                // 토큰 갱신 실패 시 로그아웃
                processQueue(refreshError, null);
                clearAuthAndRedirect();
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // 403 Forbidden은 권한 문제이므로 바로 로그아웃
        if (error.response?.status === 403) {
            clearAuthAndRedirect();
        }

        return Promise.reject(error);
    }
);

/**
 * 로그인
 */
export const login = async (email, password, rememberMe = false) => {
    try {
        const response = await authApi.post('/auth/login', {
            email,
            password,
            rememberMe
        });

        // ApiResponse 형식: { success, data: { token, user }, ... }
        const responseData = response.data.data || response.data;
        const { token, user } = responseData;

        // rememberMe에 따라 저장소 선택
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('token', token);
        storage.setItem('user', JSON.stringify(user));
        storage.setItem('rememberMe', rememberMe.toString());

        return responseData;
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
};

/**
 * 로그아웃
 */
export const logout = async () => {
    try {
        await authApi.post('/auth/logout');
    } catch (error) {
        console.error('Logout failed:', error);
    } finally {
        // 두 스토리지 모두 정리
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('rememberMe');
    }
};

/**
 * 현재 사용자 정보 조회
 */
export const getCurrentUser = async () => {
    try {
        const response = await authApi.get('/auth/me');
        // ApiResponse 형식: { success, data: userInfo, ... }
        return response.data.data || response.data;
    } catch (error) {
        console.error('Failed to get current user:', error);
        throw error;
    }
};

/**
 * 토큰 갱신
 */
export const refreshToken = async () => {
    try {
        const response = await authApi.post('/auth/refresh');
        // ApiResponse 형식: { success, data: { token }, ... }
        const token = response.data.data?.token || response.data.token;

        // 기존 저장소와 동일한 곳에 저장
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('token', token);
        return token;
    } catch (error) {
        console.error('Failed to refresh token:', error);
        throw error;
    }
};

/**
 * 로그인 상태 확인
 */
export const isAuthenticated = () => {
    const token = getToken();
    return !!token;
};

/**
 * 저장된 사용자 정보 가져오기
 */
export const getStoredUser = () => {
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (userStr) {
        try {
            return JSON.parse(userStr);
        } catch (e) {
            console.error('Failed to parse user data:', e);
            return null;
        }
    }
    return null;
};

/**
 * 사용자 역할 확인
 */
export const isAdmin = () => {
    const user = getStoredUser();
    return user && user.role === 'ADMIN';
};

export const isOperator = () => {
    const user = getStoredUser();
    return user && user.role === 'OPERATOR';
};

export const isSuperAdmin = () => {
    const user = getStoredUser();
    return user && user.role === 'SUPER_ADMIN';
};

/**
 * 권한 확인
 * @param {string} permissionCode - 확인할 권한 코드 (예: 'enterprise.view')
 * @returns {boolean}
 */
export const hasPermission = (permissionCode) => {
    const user = getStoredUser();
    if (!user) return false;

    // SUPER_ADMIN은 모든 권한 보유
    if (user.role === 'SUPER_ADMIN') return true;
    if (user.permissions && user.permissions.includes('*')) return true;

    // 권한 목록에서 확인
    return user.permissions && user.permissions.includes(permissionCode);
};

/**
 * 여러 권한 중 하나라도 있는지 확인
 * @param {string[]} permissionCodes - 확인할 권한 코드 배열
 * @returns {boolean}
 */
export const hasAnyPermission = (permissionCodes) => {
    if (!permissionCodes || permissionCodes.length === 0) return true;
    return permissionCodes.some(perm => hasPermission(perm));
};

/**
 * 모든 권한이 있는지 확인
 * @param {string[]} permissionCodes - 확인할 권한 코드 배열
 * @returns {boolean}
 */
export const hasAllPermissions = (permissionCodes) => {
    if (!permissionCodes || permissionCodes.length === 0) return true;
    return permissionCodes.every(perm => hasPermission(perm));
};

/**
 * 사용자 권한 목록 가져오기
 * @returns {string[]}
 */
export const getUserPermissions = () => {
    const user = getStoredUser();
    return user?.permissions || [];
};

export default {
    login,
    logout,
    getCurrentUser,
    refreshToken,
    isAuthenticated,
    getStoredUser,
    isAdmin,
    isOperator,
    isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getUserPermissions
};
